import { useEffect } from 'react';
import { ExpandedState } from '@tanstack/react-table';
import { FieldMappingTreeNode } from '../../../../../../utils/fieldMappingTreeBuilder';
import {
  calculateInitialExpansion,
  calculateAutoExpansionForFiltering,
} from '../../../../../../utils/fieldMappingTreeExpansion';
import { FieldFilter } from '../../../../../../utils/fieldMappingSearch';

export interface UseTreeExpansionEffectsParams {
  filteredTreeData: FieldMappingTreeNode[];
  fieldFilter: FieldFilter;
  searchQuery: string;
  userExpanded: ExpandedState;
  initialExpansionSet: React.MutableRefObject<boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setUserExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
}

/**
 * Custom hook for managing tree expansion effects
 * Handles initial expansion and auto-expansion during filtering
 */
export const useTreeExpansionEffects = ({
  filteredTreeData,
  fieldFilter,
  searchQuery,
  userExpanded,
  initialExpansionSet,
  setExpanded,
  setUserExpanded,
}: UseTreeExpansionEffectsParams): void => {
  // Calculate which rows should be expanded (only nodes with mappings)
  // Only runs once on initial data load
  useEffect(() => {
    // Only run on initial load - skip if already set
    if (initialExpansionSet.current) {
      return;
    }

    // Don't set the flag if we don't have any tree data yet (still loading)
    if (filteredTreeData.length === 0) {
      return;
    }

    const newExpanded = calculateInitialExpansion(filteredTreeData);
    setUserExpanded(newExpanded);
    setExpanded(newExpanded);

    // Mark as set after we've done the initial expansion with real data
    initialExpansionSet.current = true;
  }, [filteredTreeData, initialExpansionSet, setExpanded, setUserExpanded]);

  // Auto-expand parent nodes when filtering/searching
  useEffect(() => {
    // Skip if initial expansion hasn't been set yet
    if (!initialExpansionSet.current) {
      return;
    }

    const isFiltering = fieldFilter !== 'all' || searchQuery !== '';

    if (isFiltering) {
      // When filtering/searching, auto-expand parents of visible nodes
      const autoExpanded = calculateAutoExpansionForFiltering(filteredTreeData);
      // Merge user's expansion state with auto-expansion
      const baseExpanded =
        typeof userExpanded === 'object' && userExpanded !== null
          ? userExpanded
          : {};
      const mergedExpanded = { ...baseExpanded, ...autoExpanded };
      setExpanded(mergedExpanded);
    } else {
      // When not filtering, use only user's expansion state
      setExpanded(userExpanded);
    }
  }, [
    filteredTreeData,
    fieldFilter,
    searchQuery,
    userExpanded,
    initialExpansionSet,
    setExpanded,
  ]);
};
